import React, { useState, useCallback, useRef, useEffect } from 'react';
import Header from './components/Header';
import ImageDisplay from './components/ImageDisplay';
import Login from './components/Login';
import ConfirmationModal from './components/ConfirmationModal';
import PricingModal from './components/PricingModal';
import AboutUs from './components/AboutUs';
import LegalModal from './components/LegalModal';
import HelpModal from './components/HelpModal';
import WelcomeModal from './components/WelcomeModal';
import TourGuide, { TourStep } from './components/TourGuide';
import Icon from './components/Icon';
import Spinner from './components/Spinner';
import { useLanguage } from './context/LanguageContext';
import { useAuth } from './context/AuthContext';
import { generateImageFromPrompt } from './services/geminiService';
import { analytics } from './services/firebase';

// Tool Components
import MatchMakerTool from './components/tools/MatchMakerTool';
import NameplateTool from './components/tools/NameplateTool';
import SketchTool from './components/tools/SketchTool';
import MockupTool from './components/tools/MockupTool';

// Extend Window interface for Google Tag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export type Tool = 'match_maker' | 'nameplate' | 'sketch_to_real' | 'mockup_generator';
export type View = 'designer' | 'about';
type ProgressStep = 'analyzing' | 'matching' | 'polishing' | 'rendering' | 'idle';

const App: React.FC = () => {
    const { t } = useLanguage();
    const { user, consumeSwarna, refundSwarna, isInitialized, canGenerate } = useAuth();
    
    const [currentView, setCurrentView] = useState<View>('designer');
    const [activeTool, setActiveTool] = useState<Tool>('match_maker');
    const [isWalletOpen, setIsWalletOpen] = useState(false);
    const [walletInitialView, setWalletInitialView] = useState<'overview' | 'credits' | 'subscription'>('overview');
    
    const [status, setStatus] = useState<ProgressStep>('idle');
    const [retryMessage, setRetryMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    
    const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(() => {
        return localStorage.getItem('najeweller_last_result');
    });

    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [isLegalOpen, setIsLegalOpen] = useState(false);
    const [isHelpOpen, setIsHelpOpen] = useState(false);

    // --- ONBOARDING & TOUR STATES ---
    const [showWelcome, setShowWelcome] = useState(false);
    const [tourActive, setTourActive] = useState(false);

    useEffect(() => {
        if (user) {
            const hasSeenTutorial = localStorage.getItem('najeweller_tutorial_seen');
            if (!hasSeenTutorial) {
                // Short delay to ensure DOM is ready
                setTimeout(() => setShowWelcome(true), 1000);
            }
        }
    }, [user]);

    const handleStartTour = () => {
        setShowWelcome(false);
        setTourActive(true);
        setCurrentView('designer'); 
    };

    const handleEndTour = () => {
        setTourActive(false);
        setShowWelcome(false);
        localStorage.setItem('najeweller_tutorial_seen', 'true');
    };

    const tourSteps: TourStep[] = [
        {
            targetId: 'tour-nav-tools',
            title: t('tourStep1Title') || 'Tool Selector',
            content: t('tourStep1Desc') || 'Switch between different AI tools here. Use "Match Maker" for sets, "Nameplate" for text, or "Sketch" to realize drawings.'
        },
        {
            targetId: 'tour-tool-panel',
            title: t('tourStep2Title') || 'Design Studio',
            content: t('tourStep2Desc') || 'This is your workspace. Upload references, configure metals, gemstones, and style preferences here.'
        },
        {
            targetId: 'tour-image-display',
            title: t('tourStep3Title') || 'Preview Canvas',
            content: t('tourStep3Desc') || 'Your generated jewelry designs will appear here in high definition. You can download or try them on.'
        },
        {
            targetId: 'tour-wallet',
            title: t('tourStep4Title') || 'Swarna Wallet',
            content: t('tourStep4Desc') || 'Manage your "Swarnas" (credits) here. Check your balance or top-up to keep designing.'
        }
    ];

    const [isAutoDownload, setIsAutoDownload] = useState(() => {
        const saved = localStorage.getItem('najeweller_autodownload');
        return saved === null ? true : saved === 'true';
    });

    // --- ANALYTICS TRACKING ---
    
    // 1. Firebase Analytics
    useEffect(() => {
        if (analytics) {
            (analytics as any).logEvent('screen_view', {
                firebase_screen: currentView,
                screen_name: currentView
            });
        }
    }, [currentView]);

    useEffect(() => {
        if (analytics && currentView === 'designer') {
            (analytics as any).logEvent('select_content', {
                content_type: 'tool',
                item_id: activeTool
            });
        }
    }, [activeTool, currentView]);

    // 2. Google Tag (gtag.js) - Virtual Page Views for Flow Analysis
    useEffect(() => {
        if (typeof window.gtag === 'function') {
            const pagePath = currentView === 'designer' ? `/designer/${activeTool}` : `/${currentView}`;
            const pageTitle = currentView === 'designer' ? `Tool: ${activeTool.replace('_', ' ')}` : 'About Us';
            
            window.gtag('event', 'page_view', {
                page_title: pageTitle,
                page_path: pagePath,
                page_location: window.location.href
            });
        }
    }, [currentView, activeTool]);

    // 3. Track Wallet/Subscription Visits via gtag
    useEffect(() => {
        if (isWalletOpen && typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
                page_title: 'Wallet & Pricing',
                page_path: `/wallet/${walletInitialView}`,
                page_location: window.location.href
            });
        }
    }, [isWalletOpen, walletInitialView]);
    // --------------------------

    const toggleAutoDownload = () => {
        setIsAutoDownload(prev => {
            const newValue = !prev;
            localStorage.setItem('najeweller_autodownload', String(newValue));
            return newValue;
        });
    };

    const imageSectionRef = useRef<HTMLDivElement>(null);
    
    useEffect(() => {
        if (generatedImageUrl) {
            localStorage.setItem('najeweller_last_result', generatedImageUrl);
        } else {
            localStorage.removeItem('najeweller_last_result');
        }
    }, [generatedImageUrl]);

    const handleReset = useCallback(() => {
        setGeneratedImageUrl(null);
        setStatus('idle');
        setRetryMessage(null);
        setError(null);
    }, []);

    const handleOpenWallet = (view: 'overview' | 'credits' | 'subscription' = 'overview') => {
        setWalletInitialView(view);
        setIsWalletOpen(true);
        if (analytics) (analytics as any).logEvent('view_wallet', { view });
    };

    const autoDownloadImage = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = `najeweller-design-${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        link.remove();
    };

    const scrollToImageOnMobile = () => {
        if (window.innerWidth < 1024 && imageSectionRef.current) {
            imageSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    // Extracted Error Handling Logic (From New Code)
    const handleGenerationError = async (e: any, cost: number) => {
        console.error("Generation failed:", e);
        // Refund the optimistically deducted credits
        refundSwarna(cost, activeTool, "Generation Error"); 
        
        setError(e.message || t('errorImageGeneration') + " - Credits refunded.");

        // Smart Redirect Logic
        if (e.message) {
            if (e.message.includes("limit") || e.message.includes("upgrade")) {
                setTimeout(() => handleOpenWallet('subscription'), 1500);
            } else if (e.message.includes("Insufficient")) {
                setTimeout(() => handleOpenWallet('overview'), 1500);
            }
        }
    };

    const handleToolGeneration = async (
        cost: number, 
        generatorFunction: (onStatusUpdate: (msg: string) => void) => Promise<string>,
        customProgressSpeed: number = 1000
    ) => {
        if (!canGenerate()) {
            setError(t('errorMonthlyLimit'));
            return;
        }
        
        try {
            consumeSwarna(cost, activeTool);
            if (analytics) (analytics as any).logEvent('spend_virtual_currency', { value: cost, item_name: activeTool });
        } catch (e: any) {
            setError(e.message);
            // Smart Redirect for initial check failures
            if (e.message) {
                if (e.message.includes("limit") || e.message.includes("upgrade")) {
                    setTimeout(() => handleOpenWallet('subscription'), 1500);
                } else if (e.message.includes("Insufficient")) {
                    setTimeout(() => handleOpenWallet('overview'), 1500);
                }
            }
            return;
        }

        setError(null);
        setGeneratedImageUrl(null); 
        setRetryMessage(null);
        scrollToImageOnMobile();

        const progressSequence: ProgressStep[] = ['analyzing', 'matching', 'polishing', 'rendering'];
        let stepIdx = 0;
        setStatus(progressSequence[stepIdx]);

        const progressInterval = setInterval(() => {
            if (!retryMessage) {
                stepIdx++;
                if (stepIdx < progressSequence.length) {
                    setStatus(progressSequence[stepIdx]);
                }
            }
        }, customProgressSpeed);

        try {
            const url = await generatorFunction((msg) => setRetryMessage(msg));
            setGeneratedImageUrl(url);
            if (isAutoDownload) {
                autoDownloadImage(url);
            }
        } catch (e: any) {
            await handleGenerationError(e, cost);
        } finally {
            clearInterval(progressInterval);
            setStatus('idle');
            setRetryMessage(null);
        }
    };

    const runDesignGeneration = async (
        cost: number, 
        quality: 'standard' | 'hd', 
        promptBuilder: (onStatusUpdate?: (msg: string) => void) => Promise<{ prompt: string, images?: any[] }>
    ) => {
        await handleToolGeneration(cost, async (onStatusUpdate) => {
            const { prompt, images } = await promptBuilder(onStatusUpdate);
            const allowText = activeTool === 'nameplate';
            return await generateImageFromPrompt(prompt, quality, images, allowText, onStatusUpdate, activeTool);
        }, quality === 'standard' ? 800 : 1500);
    };

    const runMockupGeneration = async (
        cost: number, 
        quality: 'standard' | 'hd',
        generator: (onStatusUpdate?: (msg: string) => void) => Promise<string>
    ) => {
        const speed = quality === 'standard' ? 800 : 1500;
        await handleToolGeneration(cost, generator, speed);
    };

    const handleTryThisOn = () => {
        if (generatedImageUrl) {
            localStorage.setItem('najeweller_direct_mockup', generatedImageUrl);
            setGeneratedImageUrl(null);
            setActiveTool('mockup_generator');
            setCurrentView('designer');
            setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }, 100);
        }
    };

    const renderToolNavigation = () => {
        const tools: { id: Tool; label: string; icon: string }[] = [
            { id: 'match_maker', label: t('navLabelMatch'), icon: 'diamond' },
            { id: 'nameplate', label: t('navLabelNames'), icon: 'script' },
            { id: 'sketch_to_real', label: t('navLabelSketch'), icon: 'check' },
            { id: 'mockup_generator', label: t('navLabelMockup'), icon: 'sparkles' },
        ];

        return (
            <div id="tour-nav-tools" className="bg-slate-100 dark:bg-white/5 p-0.5 rounded-xl grid grid-cols-4 gap-0.5">
                {tools.map(tool => (
                    <button
                        key={tool.id}
                        onClick={() => { setActiveTool(tool.id); handleReset(); }}
                        className={`relative flex flex-col items-center justify-center gap-0.5 py-2 md:py-1.5 rounded-lg transition-all duration-300 group
                            ${activeTool === tool.id 
                                ? 'bg-white dark:bg-slate-800 shadow-sm text-slate-900 dark:text-white ring-1 ring-apple-gold' 
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5'}`}
                    >
                        <Icon name={tool.icon} className={`w-4 h-4 md:w-3.5 md:h-3.5 transition-transform group-hover:scale-110 ${activeTool === tool.id ? 'text-apple-gold' : ''}`} />
                        <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider leading-none mt-0.5">{tool.label}</span>
                    </button>
                ))}
            </div>
        );
    };

    const getToolInfo = () => {
        switch(activeTool) {
            case 'match_maker': return { title: t('toolMatchMaker'), desc: t('toolMatchMakerDesc') };
            case 'nameplate': return { title: t('toolNameplate'), desc: t('toolNameplateDesc') };
            case 'sketch_to_real': return { title: t('toolSketch'), desc: t('toolSketchDesc') };
            case 'mockup_generator': return { title: t('toolMockup'), desc: t('toolMockupDesc') };
        }
    };
    
    const toolInfo = getToolInfo();

    if (!isInitialized) {
        return <div className="min-h-screen flex items-center justify-center"><Spinner /></div>;
    }

    if (!user) {
        return <Login />;
    }

    const renderDesignerContent = () => {
        return (
            <div className="flex flex-col lg:flex-row gap-6 p-2 lg:h-full lg:pb-2">
                {/* Tools Section */}
                <div id="tour-tool-panel" className="w-full lg:w-[400px] xl:w-[450px] flex flex-col bg-white/70 dark:bg-[#1d1d1f]/70 apple-blur rounded-3xl border border-black/5 dark:border-white/5 shadow-lg lg:overflow-hidden order-1 lg:order-1 lg:h-full transition-all">
                    
                    {/* Compact Header */}
                    <div className="p-2 border-b border-black/5 dark:border-white/5 bg-white/50 dark:bg-white/5 backdrop-blur-md z-30 sticky top-0 lg:relative">
                        <div className="mb-2">
                            {renderToolNavigation()}
                        </div>
                        <div className="flex items-center justify-between px-1 animate-in fade-in slide-in-from-top-1 min-h-[32px]">
                            <div className="flex flex-col justify-center min-w-0 pr-2">
                                <h3 className="text-sm font-bold text-slate-900 dark:text-white truncate leading-tight">{toolInfo.title}</h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 truncate leading-tight">{toolInfo.desc}</p>
                            </div>
                            <button 
                                onClick={() => setIsHelpOpen(true)}
                                className="w-6 h-6 shrink-0 rounded-full bg-apple-gold/10 flex items-center justify-center text-apple-gold hover:bg-apple-gold/20 transition-colors"
                            >
                                <Icon name="help" className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                    
                    <div className="p-4 md:p-6 lg:overflow-y-auto lg:flex-grow custom-scrollbar">
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {activeTool === 'match_maker' && <MatchMakerTool key="mm" onGenerate={runDesignGeneration} isProcessing={status !== 'idle'} />}
                            {activeTool === 'nameplate' && <NameplateTool key="np" onGenerate={runDesignGeneration} isProcessing={status !== 'idle'} onError={setError} />}
                            {activeTool === 'sketch_to_real' && <SketchTool key="str" onGenerate={runDesignGeneration} isProcessing={status !== 'idle'} />}
                            {activeTool === 'mockup_generator' && <MockupTool key="mg" onGenerate={runMockupGeneration} isProcessing={status !== 'idle'} />}
                        </div>
                        {error && (
                             <div className="mt-6 p-4 rounded-xl bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/50 text-red-600 dark:text-red-400 text-sm flex justify-between items-center animate-in slide-in-from-bottom-2">
                                <span className="flex-1 font-medium">{error}</span>
                                <button onClick={() => setError(null)} className="opacity-50 hover:opacity-100 p-2"><Icon name="close" className="w-4 h-4" /></button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Image Display Section */}
                <div id="tour-image-display" ref={imageSectionRef} className="w-full h-[50vh] lg:h-full lg:flex-1 order-2 lg:order-2 bg-white/70 dark:bg-[#1d1d1f]/70 apple-blur rounded-3xl border border-black/5 dark:border-white/5 shadow-lg p-6 relative flex flex-col justify-center overflow-hidden transition-all">
                    <div className="w-full h-full relative rounded-2xl bg-slate-50/50 dark:bg-black/20 flex flex-col overflow-hidden">
                         {status !== 'idle' && (
                             <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-white/60 dark:bg-black/60 backdrop-blur-sm rounded-2xl">
                                 <div className="bg-white dark:bg-apple-black p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-4 text-center max-w-xs">
                                     <Spinner />
                                     <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 animate-pulse whitespace-pre-line">
                                         {retryMessage ? (
                                             <span className="text-amber-500 font-bold">{retryMessage}</span>
                                         ) : (
                                             <>
                                                 {status === 'analyzing' && t('progAnalyzing')}
                                                 {status === 'matching' && t('progMatching')}
                                                 {status === 'polishing' && t('progPolishing')}
                                                 {status === 'rendering' && t('progRendering')}
                                             </>
                                         )}
                                     </p>
                                 </div>
                             </div>
                         )}
                        <div className="flex-1 min-h-0 w-full p-2 relative">
                            <ImageDisplay imageUrl={generatedImageUrl} isLoading={status !== 'idle'} onDelete={() => setGeneratedImageUrl(null)} onUnlock={() => handleOpenWallet('overview')} isLocked={false} />
                        </div>

                        {generatedImageUrl && activeTool !== 'mockup_generator' && (
                            <div className="shrink-0 pb-4 px-4 w-full flex justify-center animate-in slide-in-from-bottom-4 duration-500 z-40 pointer-events-auto">
                                <button 
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleTryThisOn();
                                    }} 
                                    className="px-8 py-3 bg-apple-gold text-white rounded-full font-bold text-sm flex items-center gap-3 shadow-2xl hover:scale-105 hover:shadow-apple-gold/40 transition-all border border-white/20 whitespace-nowrap touch-manipulation"
                                >
                                    <Icon name="sparkles" className="w-4 h-4" />
                                    <span>{t('tryThisOn')}</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderMainContent = () => {
        switch(currentView) {
            case 'about': return <AboutUs />;
            case 'designer': default: return renderDesignerContent();
        }
    };

    return (
        <div className="min-h-screen lg:h-screen w-full flex flex-col bg-[#f5f5f7] dark:bg-black lg:overflow-hidden selection:bg-apple-gold selection:text-black">
            <div className="px-4 md:px-8 py-1 md:py-2 shrink-0 bg-white/80 dark:bg-black/80 backdrop-blur-md border-b border-black/5 dark:border-white/5 z-50 sticky top-0 lg:relative">
                <Header 
                    currentView={currentView} 
                    setView={setCurrentView} 
                    onOpenWallet={handleOpenWallet}
                    isAutoDownload={isAutoDownload}
                    toggleAutoDownload={toggleAutoDownload}
                />
            </div>
            <main className="flex-grow w-full max-w-[1920px] mx-auto relative lg:overflow-hidden">
                {renderMainContent()}
            </main>
            <ConfirmationModal isOpen={showResetConfirm} onClose={() => setShowResetConfirm(false)} onConfirm={handleReset} title={t('confirmResetTitle')} message={t('confirmResetMessage')} confirmLabel={t('confirmButton')} variant="warning" />
            <LegalModal isOpen={isLegalOpen} onClose={() => setIsLegalOpen(false)} />
            <HelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} activeTool={activeTool} />
            <PricingModal isOpen={isWalletOpen} onClose={() => setIsWalletOpen(false)} initialView={walletInitialView} />
            
            {/* New Onboarding Components */}
            <WelcomeModal isOpen={showWelcome} onClose={handleEndTour} onStartTour={handleStartTour} />
            <TourGuide steps={tourSteps} isOpen={tourActive} onComplete={handleEndTour} onSkip={handleEndTour} />
        </div>
    );
};

export default App;